import { useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Polls the list of followed users who are currently online (last_seen within 2 minutes)
 * every 45s. When a followed user newly appears online (wasn't in the previous list),
 * fires a subtle acid-toned toast letting the viewer know.
 *
 * Guest users are skipped (they can't follow anyone). First poll seeds the "known online"
 * set silently so we don't spam the user with pings for people already online at login.
 */
export function useFollowingPing() {
  const { user } = useAuth();
  const knownRef = useRef(null); // null = first poll (seed only, no toasts)

  useEffect(() => {
    if (!user || user === false || user.is_guest) return;
    let alive = true;

    const poll = async () => {
      try {
        const { data } = await api.get("/me/following/online");
        if (!alive) return;
        const now = new Set((data || []).map((u) => u.id));
        if (knownRef.current === null) {
          knownRef.current = now;
          return;
        }
        const known = knownRef.current;
        for (const u of data || []) {
          if (!known.has(u.id)) {
            toast(`✦ @${u.username} is online`, {
              description: "Tap to say hi.",
              style: {
                background: "#050505",
                border: "2px solid #D4FF00",
                color: "#F4F4F0",
                borderRadius: 0,
                fontFamily: "JetBrains Mono, monospace",
              },
              action: {
                label: "DM",
                onClick: () => { window.location.href = `/dm/${u.id}`; },
              },
            });
          }
        }
        knownRef.current = now;
      } catch {
        /* silent */
      }
    };

    poll();
    const t = setInterval(poll, 45000);
    return () => { alive = false; clearInterval(t); };
  }, [user]);
}
